import { useEffect } from "react";

interface SeoParams {
  title: string;
  description: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
}

export function useSeo({
  title,
  description,
  ogTitle,
  ogDescription,
  ogUrl,
  ogType,
  noIndex,
  jsonLd,
}: SeoParams) {
  useEffect(() => {
    document.title = title;

    const setMeta = (selector: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        const match = selector.match(/["']([^"']+)["']/);
        const val = match ? match[1] : "";
        const attr = selector.includes("property=")
          ? "property"
          : "name";
        el.setAttribute(attr, val);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', ogTitle || title);
    setMeta('meta[property="og:description"]', ogDescription || description);
    if (ogType) {
      setMeta('meta[property="og:type"]', ogType);
    }
    setMeta('meta[name="twitter:title"]', ogTitle || title);
    setMeta('meta[name="twitter:description"]', ogDescription || description);
    if (ogUrl) {
      setMeta('meta[property="og:url"]', ogUrl);
      const canonical = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement | null;
      if (canonical) {
        canonical.href = ogUrl;
      }
    }

    // noindex handling
    let noIndexEl = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    if (noIndex) {
      if (!noIndexEl) {
        noIndexEl = document.createElement("meta");
        noIndexEl.setAttribute("name", "robots");
        document.head.appendChild(noIndexEl);
      }
      noIndexEl.setAttribute("content", "noindex, nofollow");
    } else if (noIndexEl) {
      noIndexEl.remove();
    }

    const scripts: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((item, index) => {
        const id = `page-json-ld-${index}`;
        let existing = document.getElementById(id) as HTMLScriptElement | null;
        if (!existing) {
          existing = document.createElement("script");
          existing.id = id;
          existing.type = "application/ld+json";
          document.head.appendChild(existing);
        }
        existing.textContent = JSON.stringify(item);
        scripts.push(existing);
      });
      // Remove extra scripts if array shrunk
      let extraIndex = items.length;
      while (true) {
        const extra = document.getElementById(`page-json-ld-${extraIndex}`);
        if (!extra) break;
        extra.remove();
        extraIndex++;
      }
    }

    return () => {
      scripts.forEach((s) => s.remove());
      if (noIndexEl) noIndexEl.remove();
      // cleanup extra slots on unmount
      let idx = 0;
      while (true) {
        const el = document.getElementById(`page-json-ld-${idx}`);
        if (!el) break;
        el.remove();
        idx++;
      }
    };
  }, [title, description, ogTitle, ogDescription, ogUrl, ogType, noIndex, jsonLd]);
}